import React, { useState, useCallback } from 'react';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import classNames from 'classnames';
import { useFormikContext, getIn } from 'formik';
import { lookupsJson } from '@ourbranch/lookups';
import PropTypes from 'prop-types';

import {
  getRapaRatingSymbols,
  rapaCoverageSymbols,
  rapaSymbolUpdates,
  vehicleTypeCodeOptions,
  vehicleHorsepowerCodeOptions,
  vehicleSymbolGroupOptions
} from 'common/helpers/car-symbols-helpers';
import { appConfig } from 'brand-config';
import { Label } from 'core/components/label';
import { FormField } from 'core/components/form';
import useSession from 'core/hooks/use-session';
import caretOrange from 'core/assets/svg/caretOrange.svg';
import { yearOptions } from 'core/helpers/year-options';
import { NotificationCard } from 'core/components/notification-card';
import { ElemType, MapClassesToElem } from 'core/helpers/styles-helpers';
import darkInfoIcon from 'core/assets/svg/ic-info-black.svg';
import externalLinkIcon from 'core/assets/svg/external-link.svg';

export const AddCarDetails = ({ fieldName, classes, state }) => {
  const [open, setOpen] = useState(false);
  const { values, setFieldValue } = useFormikContext();
  // Underwriters may manually enter RAPA symbols for a vehicle Verisk can't decode (new model year,
  // incomplete vehicle); everyone else sees them read-only. #35585
  const { canApproveUWReview } = useSession();
  // Farm-carrier autos (e.g. CFM) rate on Verisk/RAPA symbols, not the Branch make/model/style scheme,
  // so those inputs are always empty for them. Show the RAPA symbols (the values sent to the rater)
  // instead of the empty make/model fields (#35042). On a RAPA brand this includes symbol-less and
  // antique cars, which the rater rates on its 'A' default rather than any stored symbol.
  const usesRapaSymbols = appConfig?.featureFlags?.autoUsesRapaSymbols ?? false;
  const rapaSymbols = getRapaRatingSymbols(getIn(values, fieldName), { usesRapaSymbols });

  // RAPA symbols are upper-case; normalize UW-entered input so it matches the pulled/rated values (the
  // rater keys factors on the exact stored character), and carry a BI/Coll edit into the legacy pair the
  // rating plan still keys on (see rapaSymbolUpdates). #35585 #36504
  const onSymbolChange = useCallback(
    (value, id) => {
      const field = id.slice(fieldName.length + 1);
      for (const [key, update] of Object.entries(rapaSymbolUpdates(field, value))) {
        setFieldValue(`${fieldName}.${key}`, update);
      }
    },
    [fieldName, setFieldValue]
  );

  const onToggle = useCallback(() => {
    setOpen(!open);
  }, [setOpen, open]);
  return (
    <Grid container className={classes.addCarDetailsForm}>
      <Grid container item className={classes.addCarDetailsButtonContainer} onClick={onToggle}>
        <Label className={classes.addCarDetailsLabel}>Additional Car Details</Label>
        <IconButton classes={MapClassesToElem(ElemType.IconButton, classes)}>
          <img
            className={classNames(classes.icon, { [classes.collapse]: open })}
            src={caretOrange}
            alt={open ? 'fold' : 'unfold'}
          />
        </IconButton>
      </Grid>
      {open && (
        <Grid className={classes.noMargin} spacing={4} container item justifyContent="space-between">
          <FormField
            type="select"
            id={`${fieldName}.year`}
            name={`${fieldName}.year`}
            label="Year"
            options={yearOptions(1981)}
            mode="light"
            xs={4}
          />
          <FormField
            type="string"
            id={`${fieldName}.make`}
            name={`${fieldName}.make`}
            label="Make"
            mode="light"
            xs={4}
          />
          <FormField
            type="string"
            id={`${fieldName}.model`}
            name={`${fieldName}.model`}
            label="Model"
            mode="light"
            xs={4}
          />
          <FormField
            type="select"
            id={`${fieldName}.antiTheftStatus`}
            name={`${fieldName}.antiTheftStatus`}
            label="Anti Theft"
            options={lookupsJson.vehicleEquipmentStatusCodes}
            mode="light"
            xs={4}
          />
          <FormField
            type="select"
            id={`${fieldName}.blindSpotStatus`}
            name={`${fieldName}.blindSpotStatus`}
            label="Blind Spot"
            options={lookupsJson.vehicleEquipmentStatusCodes}
            mode="light"
            xs={4}
          />
          <FormField
            type="select"
            id={`${fieldName}.aebStatus`}
            name={`${fieldName}.aebStatus`}
            label="AEB"
            options={lookupsJson.vehicleEquipmentStatusCodes}
            mode="light"
            xs={4}
          />
          <FormField
            type="select"
            id={`${fieldName}.singleAirbagStatus`}
            name={`${fieldName}.singleAirbagStatus`}
            label="Single Airbag"
            options={lookupsJson.vehicleEquipmentStatusCodes}
            mode="light"
            xs={4}
          />
          <FormField
            type="select"
            id={`${fieldName}.doubleAirbagStatus`}
            name={`${fieldName}.doubleAirbagStatus`}
            label="Dual Airbags"
            options={lookupsJson.vehicleEquipmentStatusCodes}
            mode="light"
            xs={4}
          />
          <FormField
            className={classes.luxuryCheckbox}
            type="checkbox"
            id={`${fieldName}.luxury`}
            name={`${fieldName}.luxury`}
            label="Luxury"
            mode="light"
            xs={4}
          />

          {rapaSymbols ? (
            canApproveUWReview ? (
              <>
                <Grid item xs={12}>
                  <Label type="infoLabel">Rating Symbols (one per coverage, UW entry)</Label>
                </Grid>
                {rapaCoverageSymbols.map(({ key, field, label }) => (
                  <FormField
                    key={field}
                    type="string"
                    id={`${fieldName}.${field}`}
                    name={`${fieldName}.${field}`}
                    label={label}
                    mode="light"
                    xs={2}
                    onChange={onSymbolChange}
                    inputProps={{ maxLength: 2 }}
                    // A blank input still rates on the coverage's fallback (a car symbolled before
                    // #36504 has only the legacy pairs), so show that symbol as a hint without saving it.
                    // Only when blank: an entered value's own validation message must not be hidden.
                    {...(getIn(values, `${fieldName}.${field}`) ? {} : { helperText: `Rating on ${rapaSymbols[key]}` })}
                  />
                ))}
              </>
            ) : (
              <>
                {rapaCoverageSymbols.map(({ key, field, label }) => (
                  <Grid key={field} item xs={2} className={classes.readOnlySymbol}>
                    <Label type="infoLabel">{label}</Label>
                    <Label type="infoValue">{rapaSymbols[key] || '—'}</Label>
                  </Grid>
                ))}
              </>
            )
          ) : (
            <>
              <NotificationCard
                type="infoGreen"
                header="Follow the steps in the linked Guru card when entering vehicle symbols manually"
                iconByHeader
                iconSize="icon16"
                icon={darkInfoIcon}
                iconClassName={classes.icon}
                noShadow
              >
                <Grid container alignItems="center">
                  Always use the Vehicle Symbol Validation Tool to check your selections—unless the symbols have already
                  been provided or approved by the UW team.{' '}
                  <a
                    href="https://app.getguru.com/card/TqA5yndc/How-to-Add-Vehicle-Manually-by-Symbols"
                    target="_blank"
                    rel="noreferrer"
                    className={classes.link}
                  >
                    <Grid container alignItems="center">
                      <img src={externalLinkIcon} alt="link to guru card" />
                      View guide on how to add vehicle symbols manually
                    </Grid>
                  </a>
                </Grid>
              </NotificationCard>
              <FormField
                type="string"
                id={`${fieldName}.symbolMake`}
                name={`${fieldName}.symbolMake`}
                label="Make Symbol"
                mode="light"
                xs={3}
              />
              <FormField
                type="string"
                id={`${fieldName}.symbolModel`}
                name={`${fieldName}.symbolModel`}
                label="Model Symbol"
                mode="light"
                xs={3}
              />
              <FormField
                type="string"
                id={`${fieldName}.symbolStyle`}
                name={`${fieldName}.symbolStyle`}
                label="Style Symbol"
                mode="light"
                xs={3}
              />
              <FormField
                type="string"
                id={`${fieldName}.symbolAux`}
                name={`${fieldName}.symbolAux`}
                label="Aux Symbol"
                mode="light"
                xs={3}
              />
              {/* Ancestry Review attributes from the versioned symbol table (#35198); the rater sends
                  its defaults (X / X / A / not convertible) when these are blank. */}
              <FormField
                type="select"
                id={`${fieldName}.vehicleTypeCode`}
                name={`${fieldName}.vehicleTypeCode`}
                label="Vehicle Type Code"
                options={vehicleTypeCodeOptions}
                mode="light"
                xs={3}
              />
              <FormField
                type="select"
                id={`${fieldName}.vehicleHorsepowerCode`}
                name={`${fieldName}.vehicleHorsepowerCode`}
                label="Horsepower Code"
                options={vehicleHorsepowerCodeOptions}
                mode="light"
                xs={3}
              />
              <FormField
                type="select"
                id={`${fieldName}.vehicleSymbolGroup`}
                name={`${fieldName}.vehicleSymbolGroup`}
                label="Symbol Group"
                options={vehicleSymbolGroupOptions}
                mode="light"
                xs={3}
              />
              <FormField
                className={classes.luxuryCheckbox}
                type="checkbox"
                id={`${fieldName}.convertible`}
                name={`${fieldName}.convertible`}
                label="Convertible"
                mode="light"
                xs={3}
              />
              {state === 'FL' && (
                <>
                  <FormField
                    type="string"
                    id={`${fieldName}.symbolPGS`}
                    name={`${fieldName}.symbolPGS`}
                    label="PGS"
                    mode="light"
                    xs={3}
                  />
                  <FormField
                    type="numeric"
                    id={`${fieldName}.symbolBI`}
                    name={`${fieldName}.symbolBI`}
                    label="BI"
                    mode="light"
                    xs={3}
                  />
                  <FormField
                    type="numeric"
                    id={`${fieldName}.symbolPD`}
                    name={`${fieldName}.symbolPD`}
                    label="PD"
                    mode="light"
                    xs={3}
                  />
                  <FormField
                    type="numeric"
                    id={`${fieldName}.symbolMed`}
                    name={`${fieldName}.symbolMed`}
                    label="MED"
                    mode="light"
                    xs={3}
                  />
                  <FormField
                    type="numeric"
                    id={`${fieldName}.symbolPIP`}
                    name={`${fieldName}.symbolPIP`}
                    label="PIP"
                    mode="light"
                    xs={3}
                  />
                  <FormField
                    type="numeric"
                    id={`${fieldName}.symbolColl`}
                    name={`${fieldName}.symbolColl`}
                    label="COLL"
                    mode="light"
                    xs={3}
                  />
                  <FormField
                    type="numeric"
                    id={`${fieldName}.symbolComp`}
                    name={`${fieldName}.symbolComp`}
                    label="COMP"
                    mode="light"
                    xs={3}
                  />
                  <FormField
                    type="numeric"
                    id={`${fieldName}.symbolUM`}
                    name={`${fieldName}.symbolUM`}
                    label="UM/UIM"
                    mode="light"
                    xs={3}
                  />
                </>
              )}
            </>
          )}
        </Grid>
      )}
    </Grid>
  );
};

AddCarDetails.propTypes = {
  fieldName: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  state: PropTypes.string.isRequired
};
